/**
 * 100 more demo visits, so the table and the visitor details panel can be
 * reviewed against a realistic day (Rahim, 13 Oct). Seeded, so every load
 * and every e2e run sees the same people, times and states.
 *
 * Spread over three weeks around today, heaviest today. Day +2 stays empty
 * (the "empty days still show" preview), and nobody arrives before Grace
 * Okafor's 07:30, so the hand-written cast keeps its order.
 *
 * The mix covers every state the panel draws: expected, signed in, signed
 * out and no-shows; requirements clear, blocking, waiting for review,
 * expiring, optional and admitted by override; returning visitors with
 * several visits (the Visits tab); multi-site hosts; access needs.
 */

import type { ApiAuditEntry, ApiVisit, ApiVisitor } from '../services/api-types';
import type { ReceptionExtras, RequirementLine } from '../visits/visit-row';

/** Mulberry32: a small, seeded PRNG. */
function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = prng(20261013);
const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)];
const chance = (p: number): boolean => rand() < p;

function at(dayOffset: number, hh: number, mm = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
}

const FIRST = [
  'Alex',
  'Amara',
  'Ben',
  'Bilal',
  'Carmen',
  'Chen',
  'Chloe',
  'Dami',
  'Elif',
  'Ewan',
  'Farah',
  'Finn',
  'Gabriel',
  'Hana',
  'Harvey',
  'Imogen',
  'Isaac',
  'Jasmine',
  'Jonas',
  'Kai',
  'Kemi',
  'Laila',
  'Leon',
  'Maya',
  'Mateo',
  'Niamh',
  'Noah',
  'Olu',
  'Orla',
  'Pavel',
  'Rafael',
  'Rhys',
  'Rosa',
  'Sana',
  'Sienna',
  'Tariq',
  'Theo',
  'Uma',
  'Victor',
  'Wren',
  'Yara',
  'Yusuf',
  'Zainab',
  'Zoe',
  'Arjun',
  'Beatriz',
  'Callum',
  'Dalia',
  'Erik',
  'Freya',
] as const;

const LAST = [
  'Adeyemi',
  'Andersson',
  'Bakker',
  'Bianchi',
  'Brennan',
  'Chowdhury',
  'Costa',
  'Dubois',
  'Evans',
  'Farouk',
  'Fischer',
  'Garcia',
  'Haddad',
  'Hughes',
  'Ito',
  'Jensen',
  'Kaur',
  'Kowalski',
  'Lindqvist',
  'Mahmoud',
  'Martins',
  'Mensah',
  'Moreau',
  'Murphy',
  'Novak',
  'Okeke',
  'Olsen',
  'Patel',
  'Quinn',
  'Reyes',
  'Rossi',
  'Sato',
  'Schmidt',
  'Silva',
  'Taylor',
  'Varga',
  'Walsh',
  'Weber',
  'Yilmaz',
  'Zhang',
] as const;

interface TypeProfile {
  readonly id: string;
  readonly name: string;
  readonly companies: readonly string[];
  readonly jobs: readonly string[];
  readonly weight: number;
  /** Requirement sets this type can carry. */
  readonly requirements: readonly (readonly string[])[];
}

const TYPES: readonly TypeProfile[] = [
  {
    id: 'vt-guest',
    name: 'Standard guest',
    companies: [
      'Stora Enso',
      'Speechmark Media',
      'Brightline Studio',
      'Northgate Legal',
      'Arcadis',
      'KPMG',
      'Atkins',
    ],
    jobs: [
      'Account Director',
      'Consultant',
      'Product Manager',
      'Solicitor',
      'Engineer',
      'Designer',
    ],
    weight: 40,
    requirements: [[], ['Photo ID'], ['Photo ID', 'Visitor NDA']],
  },
  {
    id: 'vt-contractor',
    name: 'Contractor',
    companies: [
      'Meridian Facilities',
      'Kier Group',
      'Mitie',
      'ISS Facility Services',
      'Bam Construct',
    ],
    jobs: ['HVAC Engineer', 'Electrician', 'Site Supervisor', 'Fire Safety Engineer', 'Plumber'],
    weight: 22,
    requirements: [
      ['Photo ID', 'Site safety induction', 'Public liability insurance', 'Hot works permit'],
      ['Photo ID', 'Site safety induction', 'CSCS card'],
      ['Photo ID', 'Site safety induction', 'Public liability insurance'],
    ],
  },
  {
    id: 'vt-supplier',
    name: 'Supplier',
    companies: ['Fresh Fork Catering', 'Bunzl', 'Office Depot', 'Greenleaf Plants', 'Brakes'],
    jobs: ['Delivery Driver', 'Account Manager', 'Events Lead', 'Service Technician'],
    weight: 16,
    requirements: [['Photo ID', 'Hygiene certificate'], ['Photo ID']],
  },
  {
    id: 'vt-interview',
    name: 'Interview candidate',
    companies: [''],
    jobs: [''],
    weight: 10,
    requirements: [[], ['Photo ID']],
  },
  {
    id: 'vt-starter',
    name: 'New starter',
    companies: ['Oceanic Metals'],
    jobs: ['Data Analyst', 'Graduate Engineer', 'HR Advisor', 'Finance Associate'],
    weight: 7,
    requirements: [['Photo ID', 'Right to work']],
  },
  {
    id: 'vt-vip',
    name: 'VIP',
    companies: ['VodafoneThree', 'Rolls-Royce', 'HSBC', 'Birmingham City Council'],
    jobs: ['Chief Executive', 'Managing Director', 'Board Member'],
    weight: 5,
    requirements: [[]],
  },
];

const SITES = [
  { _id: 'loc-rrw', name: 'Birmingham Rum Runner Works', printer: 'Rum Runner Works reception' },
  { _id: 'loc-spm', name: 'Speechmark', printer: 'Speechmark reception' },
  { _id: 'loc-hop', name: 'Hopper House', printer: 'Hopper House reception' },
] as const;

const HOSTS = [
  { userId: 'h-marcus', name: 'Marcus Webb', email: 'marcus.webb@oceanicmetals.com' },
  { userId: 'h-dianne', name: 'Dianne Russell', email: 'dianne.russell@oceanicmetals.com' },
  { userId: 'h-dean', name: 'Dean Bagnall', email: 'dean.bagnall@oceanicmetals.com' },
  { userId: 'h-nathan', name: 'Nathan Thomas', email: 'nathan.thomas@oceanicmetals.com' },
  { userId: 'h-nadia', name: 'Nadia Hassan', email: 'nadia.hassan@oceanicmetals.com' },
  { userId: 'h-ruth', name: 'Ruth Coker', email: 'ruth.coker@oceanicmetals.com' },
  { userId: 'h-ellen', name: 'Ellen Pierce', email: 'ellen.pierce@oceanicmetals.com' },
  { userId: 'h-facilities', name: 'Facilities Team', email: 'facilities@oceanicmetals.com' },
  { userId: 'h-people', name: 'People Team', email: 'people@oceanicmetals.com' },
] as const;

const ACCESS = ['Wheelchair user', 'Hearing loop', 'Step-free route', 'Assistance dog'] as const;
const OPERATORS = ['Sam Patel', 'Ruth Walker', 'Nadia Hassan'] as const;
const REASONS = [
  'Host escorting all day, certificate to follow',
  'Emergency call-out, supervisor on site',
  'Original seen by the host, upload pending',
] as const;

function weightedType(): TypeProfile {
  const total = TYPES.reduce((s, t) => s + t.weight, 0);
  let r = rand() * total;
  for (const t of TYPES) {
    r -= t.weight;
    if (r <= 0) return t;
  }
  return TYPES[0];
}

/** Days from today, heaviest today; never +2 (the empty day). */
const DAY_PLAN: readonly number[] = [
  ...Array(34).fill(0),
  ...Array(10).fill(1),
  ...[3, 3, 3, 4, 4, 5, 6, 7, 8, 9, 10],
  ...Array(8).fill(-1),
  ...[-2, -2, -3, -3, -4, -5, -6, -7, -7, -8, -9, -10, -10],
];

function short(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function metLine(name: string, day: number): RequirementLine {
  const when = day >= 0 ? `Checked ${short(Math.min(day, 0) - 1)}` : `Checked ${short(day)}`;
  return {
    name,
    mark: 'met',
    detail: name === 'Visitor NDA' ? `Signed ${short(day - 3)}` : when,
    blocking: true,
  };
}

type AdmissionMix = 'clear' | 'blocking' | 'review' | 'warning' | 'override';

function requirementLines(
  names: readonly string[],
  mix: AdmissionMix,
  day: number,
): RequirementLine[] {
  return names.map((name, i) => {
    const optional = name === 'Hot works permit';
    if (optional) {
      return mix === 'clear'
        ? metLine(name, day)
        : { name, mark: 'optional', detail: 'Not seen · optional', blocking: false };
    }
    if (i === 0 || mix === 'clear') return metLine(name, day);
    if ((mix === 'blocking' || mix === 'override') && i === 1) {
      return {
        name,
        mark: 'missing',
        detail: `Expired ${short(-90 - Math.floor(rand() * 200))}`,
        blocking: true,
      };
    }
    if ((mix === 'review' || mix === 'blocking') && i === 2) {
      return {
        name,
        mark: 'checking',
        detail: `Uploaded ${short(-1)} · waiting for review`,
        blocking: true,
      };
    }
    if (mix === 'warning' && i === names.length - 1) {
      return { name, mark: 'outstanding', detail: 'Not signed · not blocking', blocking: false };
    }
    return metLine(name, day);
  });
}

interface Generated {
  readonly visitors: ApiVisitor[];
  readonly visits: ApiVisit[];
  readonly reception: Record<string, ReceptionExtras>;
  readonly audit: Record<string, ApiAuditEntry[]>;
}

function generate(): Generated {
  const visitors: ApiVisitor[] = [];
  const visits: ApiVisit[] = [];
  const reception: Record<string, ReceptionExtras> = {};
  const audit: Record<string, ApiAuditEntry[]> = {};
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // 72 people; the first 20 come back, so the Visits tab has history.
  for (let i = 0; i < 72; i++) {
    const type = weightedType();
    const first = FIRST[i % FIRST.length];
    const last = LAST[(i * 7 + 3) % LAST.length];
    const company = pick(type.companies) || null;
    const domain =
      (company ?? 'gmail').toLowerCase().replace(/[^a-z]/g, '') + (company ? '.co.uk' : '.com');
    visitors.push({
      _id: `p-gen-${i}`,
      firstName: first,
      lastName: last,
      email: `${first}.${last}@${domain}`.toLowerCase(),
      mobile: `07${String(100000000 + Math.floor(rand() * 899999999)).slice(0, 3)} ${String(Math.floor(rand() * 900000) + 100000)}`,
      countryCode: '+44',
      company,
      jobTitle: pick(type.jobs) || null,
      carRegistration: chance(0.35)
        ? `${pick(['BX', 'WM', 'FD', 'LR', 'KP'])}${20 + Math.floor(rand() * 5)} ${pick(['KLT', 'HXM', 'RTA', 'JPO', 'VNE'])}`
        : null,
      accessNeeds: chance(0.12) ? pick(ACCESS) : null,
      tags: [],
      vip: type.id === 'vt-vip',
      isPrivate: type.id === 'vt-interview' && chance(0.5),
      // One-off visitors: raised by a visit, never saved to the address book,
      // so the book shows the spec's sample contacts (address-book.md).
      isInAddressBook: false,
      pin: chance(0.6) ? String(1000 + Math.floor(rand() * 9000)) : null,
      createdAt: at(-30 - Math.floor(rand() * 300), 9),
      visitorTypeId: type.id,
      visitorType: type.name,
    });
  }

  // Round-robin through the people, never the same person twice on a day:
  // 100 visits over 72 people, so 28 come back on another day.
  const onDay = new Map<number, Set<number>>();
  let next = 0;
  const personFor = (day: number): ApiVisitor => {
    const used = onDay.get(day) ?? new Set<number>();
    while (used.has(next % visitors.length)) next++;
    const index = next % visitors.length;
    used.add(index);
    onDay.set(day, used);
    next++;
    return visitors[index];
  };

  DAY_PLAN.forEach((day, n) => {
    const who = personFor(day);
    const type = TYPES.find((t) => t.id === who.visitorTypeId) ?? TYPES[0];
    const site = pick(SITES);
    const host = pick(HOSTS);
    const organiser = chance(0.7) ? host : pick(HOSTS);
    const startH = 7 + Math.floor(rand() * 9);
    const startM = pick([0, 15, 30, 45]);
    const arriveH = startH === 7 ? 8 : startH;
    const length = pick([1, 1.5, 2, 3, 4, 6, 8]);
    const endMinutes = Math.min(arriveH * 60 + startM + length * 60, 19 * 60);
    const startMinutes = arriveH * 60 + startM;

    // Where the day stands.
    let status: 'expected' | 'signedIn' | 'signedOut' | 'noShow' = 'expected';
    if (day < 0) status = chance(0.86) ? 'signedOut' : 'noShow';
    if (day === 0) {
      if (startMinutes + 20 < nowMinutes) {
        status =
          endMinutes < nowMinutes
            ? chance(0.8)
              ? 'signedOut'
              : 'noShow'
            : chance(0.82)
              ? 'signedIn'
              : 'expected';
      } else if (chance(0.12)) {
        status = 'signedIn'; // early arrivals
      }
    }

    const names = pick(type.requirements);
    const mix: AdmissionMix =
      names.length === 0
        ? 'clear'
        : status === 'signedIn' && names.length > 2 && chance(0.25)
          ? 'override'
          : status === 'expected' && names.length > 1
            ? pick(['clear', 'clear', 'blocking', 'review', 'warning'] as const)
            : chance(0.15) && names.length > 1
              ? 'warning'
              : 'clear';
    const lines = requirementLines(names, mix, day);
    const blocking = lines.filter((l) => l.blocking && l.mark !== 'met').map((l) => l.name);
    const warnings = lines
      .filter((l) => !l.blocking && l.mark !== 'met' && l.mark !== 'optional')
      .map((l) => l.name);

    const signInMin = startMinutes - 10 + Math.floor(rand() * 25);
    const signedIn = status === 'signedIn' || status === 'signedOut';
    const signInTime = signedIn ? at(day, Math.floor(signInMin / 60), signInMin % 60) : null;
    const outMin = endMinutes - 20 + Math.floor(rand() * 40);
    const signOutTime =
      status === 'signedOut' ? at(day, Math.floor(outMin / 60), outMin % 60) : null;
    const id = `v-gen-${n}`;
    const badge = signedIn ? String(1030302400 + n) : null;

    visits.push({
      _id: id,
      locationId: site._id,
      visitorId: who._id,
      expectedDate: at(day, arriveH, startM),
      expectedEnd: at(day, Math.floor(endMinutes / 60), endMinutes % 60),
      visiting: host,
      hosts: [
        { ...host, role: 'host' },
        ...(organiser.userId === host.userId ? [] : [{ ...organiser, role: 'organiser' }]),
      ],
      signInStatus: status,
      checkInStatus:
        status === 'signedIn' ? 'checkedIn' : status === 'signedOut' ? 'checkedOut' : null,
      questionnaireStatus: null,
      badgeNumber: badge,
      notes: chance(0.2)
        ? pick([
            'Meeting room 3.02 booked.',
            'Bring the laptop loan form.',
            'Lunch with the team.',
            'Escort to the plant room.',
          ])
        : null,
      tags: chance(0.25)
        ? [pick(['Escort required', 'Hi-vis', 'Deliveries', 'Legal', 'Interview'])]
        : [],
      signInTime,
      signOutTime,
      signInLog: [
        ...(signInTime
          ? [{ timestamp: signInTime, status: 'signedIn', method: pick(['manual', 'kiosk']) }]
          : []),
        ...(signOutTime ? [{ timestamp: signOutTime, status: 'signedOut', method: 'kiosk' }] : []),
        ...(status === 'noShow'
          ? [{ timestamp: at(day, 23, 0), status: 'noShow', method: 'system' }]
          : []),
      ],
      checkInLog: [],
      verification: { verified: blocking.length === 0 },
      sendInvitation: true,
      visitor: who,
      location: { _id: site._id, name: site.name },
      visitorTypeId: who.visitorTypeId,
      visitorType: who.visitorType,
    });

    const sentAt = at(day - 2 - Math.floor(rand() * 5), 9 + Math.floor(rand() * 8), 0);
    reception[id] = {
      verification:
        names.length === 0
          ? { kind: 'none' }
          : mix === 'override'
            ? { kind: 'override' }
            : blocking.length
              ? {
                  kind: 'blocking',
                  requirements: blocking,
                  held: lines.filter((l) => l.mark === 'met').map((l) => l.name),
                }
              : warnings.length
                ? { kind: 'warning', requirements: warnings }
                : { kind: 'clear' },
      requirements: lines,
      override:
        mix === 'override'
          ? {
              by: pick(OPERATORS),
              at: signInTime ?? at(day, arriveH, startM),
              reason: pick(REASONS),
            }
          : null,
      invitation: day > 3 && chance(0.3) ? 'scheduled' : pick(['sent', 'delivered', 'delivered']),
      invite: { sentAt, sentBy: organiser.name, ...(chance(0.7) ? { deliveredAt: sentAt } : {}) },
      questionnaire:
        type.id === 'vt-contractor'
          ? signedIn || chance(0.6)
            ? 'complete'
            : 'sent'
          : 'notRequired',
      accessNeeds: who.accessNeeds ? [who.accessNeeds] : [],
      badgePrint: signedIn ? (chance(0.9) ? 'printed' : 'failed') : 'notPrinted',
      badgeJob: {
        printer: site.printer,
        printerReady: chance(0.92),
        ...(signedIn ? { at: signInTime ?? undefined } : {}),
      },
      bookings: chance(0.4) ? [pick(['desk', 'parking', 'room', 'locker'] as const)] : [],
      pin: who.pin ?? null,
    };

    audit[id] = [
      {
        _id: `a-gen-${n}-1`,
        action: 'created',
        category: 'access',
        detail: `Visit created for ${who.firstName} ${who.lastName}`,
        performedByName: organiser.name,
        timestamp: at(day - 3 - Math.floor(rand() * 6), 10 + Math.floor(rand() * 6), 12),
      },
      {
        _id: `a-gen-${n}-2`,
        action: 'inviteSent',
        category: 'access',
        detail: `Invite sent to ${who.email}`,
        performedByName: organiser.name,
        timestamp: sentAt,
      },
    ];
  });

  return { visitors, visits, reception, audit };
}

export const GENERATED = generate();
